import { useEffect, useRef } from 'react'
import ApexCharts from 'apexcharts'
import { useThemeMode } from '_metronic/partials'

interface JobStatisticsChartProps {
  categories: string[]
  series: Array<{ name: string; saleUserId: string; data: number[] }>
  detail: Array<{
    date: string
    saleUserId: string
    saleName: string
    projectId: number
    division: string
    customerTitle: string
  }>
}

const JobStatisticsChart: React.FC<JobStatisticsChartProps> = ({
  categories = [],
  series = [],
  detail = [],
}) => {
  const chartRef = useRef<HTMLDivElement | null>(null)
  const { mode } = useThemeMode()

  const apexSeries = series.map(s => ({
    name: s.name,
    data: s.data,
  }))

  const options: ApexCharts.ApexOptions = {
    series: apexSeries,
    chart: {
      type: 'bar',
      stacked: true,
      height: 380,
      toolbar: { show: false },
      events: {
        // Click bar -> you can open a modal/table of detail rows for that (date, sale)
        dataPointSelection: (event, chartContext, config) => {
          const date = categories[config.dataPointIndex]
          const sale = series[config.seriesIndex]
          const rows = detail.filter(d => d.date === date && d.saleUserId === sale.saleUserId)

          console.log('Selected:', { date, sale: sale.name, rows })
          // TODO: openModal(rows)
        },
      },
    },

    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '55%',
        borderRadius: 6,
      },
    },

    dataLabels: { enabled: false },

    xaxis: {
      categories: categories,
      labels: {
        rotate: -30,
        trim: false, // important: don't cut label text
        hideOverlappingLabels: false,
      },
    },

    yaxis: {
      title: { text: 'Projects' },
    },

    legend: {
      position: 'right',
    },

    tooltip: {
      shared: false,
      intersect: true,
      custom: ({ seriesIndex, dataPointIndex }) => {
        const date = categories[dataPointIndex]
        const sale = series[seriesIndex]
        const total = sale.data[dataPointIndex] ?? 0

        const rows = detail
          .filter(d => d.date === date && d.saleUserId === sale.saleUserId)
          .slice(0, 6)

        const listHtml = rows
          .map(
            r => `
              <div style="margin-top:8px;">
                <div style="font-weight:700;">${r.division || '-'}</div>
                <div style="opacity:.9;font-size:12px;">${r.customerTitle || '-'}</div>
                <div style="opacity:.7;font-size:11px;">Project #${r.projectId}</div>
              </div>
            `
          )
          .join('')

        return `
          <div style="padding:12px; width:340px;">
            <div style="display:flex; justify-content:space-between; gap:12px;">
              <div style="font-weight:800;">${sale.name}</div>
              <div style="opacity:.75;">${date}</div>
            </div>
            <div style="margin-top:6px;">Total projects: <b>${total}</b></div>
            <div style="margin-top:10px; border-top:1px solid #e5e7eb; padding-top:10px;">
              ${listHtml || `<div style="opacity:.8;">No detail</div>`}
            </div>
          </div>
        `
      },
    },
  }

  const refreshChart = () => {
    if (!chartRef.current) {
      return
    }

    const chart = new ApexCharts(chartRef.current, options)
    if (chart) {
      chart.render()
    }

    return chart
  }

  useEffect(() => {
    const chart = refreshChart()

    return () => {
      if (chart) {
        chart.destroy()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartRef, mode, categories.join(','), JSON.stringify(series), JSON.stringify(detail)])

  return (
    <div
      ref={chartRef}
      id='kt_charts_job_statistics_chart'
      style={{ height: '400px' }}
      className='card-rounded-bottom'></div>
  )
}

export default JobStatisticsChart
